import { useMemo } from 'react'
import { useAdventureEditorStore } from '../stores/adventureEditorStore'
import { useWorkflowStore } from '@store/workflowStore'
import { usePublishedPermissions } from './usePublishedPermissions'
import { AdventureStatus, AdventureMainCategory, isTrailCategory } from '../types/adventure-editor.types'
import { FlowVersionStatus } from '../types/workflow.types'
import { isWorkflowAllowed } from '../types/category.types'

export interface PublishReadinessItem {
  id: string
  label: string
  met: boolean
  description?: string
}

export interface PublishReadiness {
  isReady: boolean
  canAttemptPublish: boolean // Has permission and correct status
  items: PublishReadinessItem[]
  blockers: string[] // Short list of what's blocking publish
}

/**
 * Hook to calculate publish readiness and provide detailed feedback.
 * This consolidates all publish validation logic in one place.
 */
export function usePublishReadiness(): PublishReadiness {
  const { canPublish, isPublished } = usePublishedPermissions()
  const adventure = useAdventureEditorStore((s) => s.adventure)
  const generalInfo = useAdventureEditorStore((s) => s.generalInfo)
  const availableCategories = useAdventureEditorStore((s) => s.availableCategories)
  const categorySettings = useAdventureEditorStore((s) => s.categorySettings)
  const nodes = useWorkflowStore((s) => s.nodes)
  const flowVersionStatus = useWorkflowStore((s) => s.versionStatus)
  const flowIsCompiled = useWorkflowStore((s) => s.isCompiled)

  return useMemo(() => {
    const items: PublishReadinessItem[] = []
    const blockers: string[] = []

    // Check adventure status
    const status = adventure?.status
    const numStatus = typeof status === 'string' ? parseInt(status, 10) : status
    const isDraft = numStatus === AdventureStatus.Draft || numStatus === undefined || isNaN(numStatus as number)
    const isInTesting = numStatus === AdventureStatus.Testing

    // Check if this type can publish directly from draft (no workflow)
    const isGeocache = generalInfo.mainCategory === AdventureMainCategory.TraditionalGeoCaches
    const isSilentAdventure = generalInfo.mainCategory === AdventureMainCategory.SilentAdventure
    const canPublishFromDraft = isGeocache || isSilentAdventure

    // Check if this adventure type has a workflow
    const hasWorkflowFeature = categorySettings?.workflowAllowed !== false &&
      (categorySettings === null ? isWorkflowAllowed(generalInfo.mainCategory) : true)

    // Check flow status for workflow-based adventures
    const flowIsInTestingOrPublished = flowVersionStatus === FlowVersionStatus.Testing ||
      flowVersionStatus === FlowVersionStatus.Published

    // 1. Status check - for workflow adventures, check FLOW status; for others check adventure status
    let statusMet: boolean
    let statusDescription: string

    if (isPublished) {
      statusMet = false
      statusDescription = 'Already published'
    } else if (canPublishFromDraft) {
      // Geocaches/Silent can publish from draft
      statusMet = isDraft || isInTesting
      statusDescription = statusMet ? 'Ready to publish' : 'Must be in Draft or Testing status'
    } else if (hasWorkflowFeature) {
      // Workflow adventures: check flow status
      statusMet = flowIsInTestingOrPublished
      if (flowVersionStatus === FlowVersionStatus.Published) {
        statusDescription = 'Flow published - ready to publish adventure'
      } else if (flowVersionStatus === FlowVersionStatus.Testing) {
        statusDescription = 'Flow in testing - ready to publish adventure'
      } else if (flowIsCompiled) {
        statusDescription = 'Flow compiled - start testing in workflow editor first'
      } else {
        statusDescription = 'Compile and test workflow first'
      }
    } else {
      // Non-workflow, non-geocache: use adventure testing status
      statusMet = isInTesting
      statusDescription = isInTesting ? 'In Testing' : 'Must be in Testing status'
    }

    if (isPublished) {
      items.push({
        id: 'status',
        label: 'Adventure status',
        met: false,
        description: 'Already published',
      })
      blockers.push('Already published')
    } else if (!statusMet) {
      items.push({
        id: 'status',
        label: hasWorkflowFeature ? 'Workflow status' : 'Adventure status',
        met: false,
        description: statusDescription,
      })
      blockers.push(statusDescription)
    } else {
      items.push({
        id: 'status',
        label: hasWorkflowFeature ? 'Workflow status' : 'Adventure status',
        met: true,
        description: statusDescription,
      })
    }

    // 2. Title check (>= 3 characters)
    const titleMet = !!(generalInfo.title && generalInfo.title.length >= 3)
    items.push({
      id: 'title',
      label: 'Title',
      met: titleMet,
      description: titleMet ? `"${generalInfo.title}"` : 'Must be at least 3 characters',
    })
    if (!titleMet) {
      blockers.push('Title too short')
    }

    // 3. Required tags check
    const selectedTags = adventure?.tags || []
    const missingCategories: string[] = []

    if (availableCategories.length > 0) {
      availableCategories.forEach((category) => {
        if (category.selectionRequired && category.isActive) {
          const hasSelection = category.tags.some((tag) => selectedTags.includes(tag.id))
          if (!hasSelection) {
            missingCategories.push(category.displayName)
          }
        }
      })
    }

    const tagsMet = missingCategories.length === 0 && availableCategories.length > 0
    const tagsLoading = availableCategories.length === 0

    items.push({
      id: 'tags',
      label: 'Required tags',
      met: tagsMet,
      description: tagsLoading
        ? 'Loading tags...'
        : tagsMet
        ? 'All required tags selected'
        : `Missing: ${missingCategories.join(', ')}`,
    })
    if (!tagsMet && !tagsLoading) {
      blockers.push(`Missing tags: ${missingCategories.join(', ')}`)
    }

    // 4. Workflow check (for Trail adventures only)
    const isTrail = isTrailCategory(generalInfo.mainCategory)
    if (isTrail) {
      const hasWorkflow = nodes && nodes.length > 1
      items.push({
        id: 'workflow',
        label: 'Workflow',
        met: hasWorkflow,
        description: hasWorkflow
          ? `${nodes.length} nodes configured`
          : 'Trail adventures require at least one waypoint',
      })
      if (!hasWorkflow) {
        blockers.push('No workflow nodes')
      }
    }

    // Calculate overall readiness
    const isReady = items.every((item) => item.met)
    const canAttemptPublish = canPublish

    return {
      isReady,
      canAttemptPublish,
      items,
      blockers,
    }
  }, [adventure, generalInfo, availableCategories, categorySettings, nodes, canPublish, isPublished, flowVersionStatus, flowIsCompiled])
}
